/**
 * API Client — Sippar
 *
 * Instancia de Axios centralizada con:
 * - Timeout de 30s (satelital: ~1200ms latencia + retransmisiones)
 * - Retry automático con backoff exponencial (3 intentos)
 * - Solo reintenta errores de red y 5xx (NO 4xx)
 * - Inyección automática de JWT desde localStorage
 * - Headers de idioma
 */

import axios from 'axios';
import axiosRetry from 'axios-retry';
import { environment } from '../environment';

// ─── Instancia dedicada ────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: environment.api.host,
  timeout: 30_000, // 30s — razonable para latencia satelital
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Retry con backoff exponencial ─────────────────────────────────────

// Rastrear errores de red consecutivos por URL para detectar fallos persistentes
// (ej: certificado inválido) y dejar de reintentar.
const consecutiveNetworkErrors = new Map<string, number>();

axiosRetry(apiClient, {
  retries: 3,
  retryDelay: (retryCount) => {
    // Backoff exponencial: 2s → 4s → 8s
    const delay = Math.pow(2, retryCount) * 1000;
    console.log(`[apiClient] Retry #${retryCount} en ${delay}ms`);
    return delay;
  },
  retryCondition: (error) => {
    // Nunca reintentar errores 4xx (autenticación, validación, etc.)
    if (error.response && error.response.status < 500) return false;

    // Reintentar errores 5xx
    if (error.response && error.response.status >= 500) return true;

    // Timeout → reintentar
    if (error.code === 'ECONNABORTED') return true;

    // Error de red sin respuesta — podría ser transitorio (pérdida de paquetes)
    // o persistente (certificado inválido, DNS, CORS).
    // Si la misma URL falla en red más de 1 vez seguida, dejar de reintentar.
    if (!error.response) {
      const url = error.config?.url || 'unknown';
      const count = (consecutiveNetworkErrors.get(url) || 0) + 1;
      consecutiveNetworkErrors.set(url, count);

      if (count > 1) {
        // Error persistente (ej: ERR_CERT_AUTHORITY_INVALID) — no reintentar
        consecutiveNetworkErrors.delete(url);
        return false;
      }
      return true;
    }

    return false;
  },
  onRetry: (retryCount, error, requestConfig) => {
    console.warn(
      `[apiClient] Reintentando petición (${retryCount}/3):`,
      requestConfig.method?.toUpperCase(),
      requestConfig.url,
      error.message
    );
  },
});

// ─── Interceptor de Request: JWT + idioma ──────────────────────────────
apiClient.interceptors.request.use(
  (config) => {
    // Token de autenticación
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user?.access_token) {
          config.headers.Authorization = `Bearer ${user.access_token}`;
        }
      }
    } catch {
      // JSON corrupto — ignorar
    }

    // Idioma
    const lang = localStorage.getItem('language') || navigator.language.split('-')[0];
    config.headers['x-custom-lang'] = lang;
    config.headers['Accept-Language'] = lang;

    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Interceptor de Response: log de errores + limpiar contadores ──────
apiClient.interceptors.response.use(
  (response) => {
    // Respuesta exitosa → limpiar contador de errores consecutivos para esta URL
    const url = response.config?.url;
    if (url) consecutiveNetworkErrors.delete(url);
    return response;
  },
  (error) => {
    if (error.code === 'ECONNABORTED') {
      console.error('[apiClient] Timeout alcanzado:', error.config?.url);
    } else if (!error.response) {
      console.error('[apiClient] Error de red:', error.message, error.config?.url);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
